import React from 'react';
import { Button, Form, Input } from 'antd-mobile';
import {
  LeftOutlined,
  LockOutlined,
  MobileOutlined,
  SafetyCertificateOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RegisterFormValues } from './types';
import { registerPageStyles } from './styles';

interface RegisterViewProps {
  loading: boolean;
  referralCode: string;
  onSubmit: (values: RegisterFormValues) => Promise<void>;
}

export const RegisterView: React.FC<RegisterViewProps> = ({
  loading,
  referralCode,
  onSubmit,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');

  return (
    <div style={registerPageStyles.page}>
      <div style={registerPageStyles.glowPrimary} />
      <div style={registerPageStyles.glowSecondary} />
      <div style={registerPageStyles.shell}>
        <div style={registerPageStyles.backRow}>
          <div style={registerPageStyles.backButton} onClick={() => navigate(-1)}>
            <LeftOutlined />
          </div>
          <div style={registerPageStyles.badge}>{t('register.promoterApp')}</div>
          <div style={{ width: 38 }} />
        </div>

        <div style={registerPageStyles.title}>{t('register.signUpTitle')}</div>
        <div style={registerPageStyles.subtitle}>{t('register.subtitle')}</div>

        <div style={registerPageStyles.heroCard}>
          <div style={registerPageStyles.heroRow}>
            <div style={registerPageStyles.heroItem}>
              <div style={registerPageStyles.heroValue}>{t('register.hero1Value')}</div>
              <div style={registerPageStyles.heroLabel}>{t('register.hero1Label')}</div>
            </div>
            <div style={registerPageStyles.heroItem}>
              <div style={registerPageStyles.heroValue}>{t('register.hero2Value')}</div>
              <div style={registerPageStyles.heroLabel}>{t('register.hero2Label')}</div>
            </div>
            <div style={registerPageStyles.heroItem}>
              <div style={registerPageStyles.heroValue}>{t('register.hero3Value')}</div>
              <div style={registerPageStyles.heroLabel}>{t('register.hero3Label')}</div>
            </div>
          </div>
          <div style={registerPageStyles.heroHint}>
            {t('register.hint')}
          </div>
        </div>

        <div style={registerPageStyles.formCard}>
          <div style={registerPageStyles.sectionTitle}>{t('register.createAccount')}</div>
          <div style={registerPageStyles.sectionSubtitle}>{t('register.sectionSubtitle')}</div>
        <Form
          layout="vertical"
          initialValues={{ referral_code: referralCode }}
          onFinish={async (values) => {
            await onSubmit(values as RegisterFormValues);
          }}
          footer={
            <Button block type="submit" color="primary" loading={loading} style={registerPageStyles.submitButton}>
              {t('register.submit')}
            </Button>
          }
        >
          <Form.Item name="username" rules={[{ required: true, message: t('register.usernameRequired') }]}>
            <div style={registerPageStyles.inputShell}>
              <div style={registerPageStyles.inputIcon}>
                <MobileOutlined />
              </div>
              <Input placeholder={t('register.usernamePlaceholder')} clearable style={registerPageStyles.input} />
            </div>
          </Form.Item>
          <Form.Item name="display_name" rules={[{ required: true, message: t('register.displayNameRequired') }]}>
            <div style={registerPageStyles.inputShell}>
              <div style={registerPageStyles.inputIcon}>
                <UserOutlined />
              </div>
              <Input placeholder={t('register.displayNamePlaceholder')} clearable style={registerPageStyles.input} />
            </div>
          </Form.Item>
          <Form.Item name="referral_code">
            <div style={registerPageStyles.inputShell}>
              <div style={registerPageStyles.inputIcon}>
                <SafetyCertificateOutlined />
              </div>
              <Input
                placeholder={t('register.referralCodePlaceholder')}
                readOnly={Boolean(referralCode)}
                clearable={!referralCode}
                style={registerPageStyles.input}
              />
            </div>
          </Form.Item>
          <Form.Item name="password" rules={[{ required: true, message: t('register.passwordRequired') }]}>
            <div style={registerPageStyles.inputShell}>
              <div style={registerPageStyles.inputIcon}>
                <LockOutlined />
              </div>
              <Input placeholder={t('register.passwordPlaceholder')} type="password" clearable style={registerPageStyles.input} />
            </div>
          </Form.Item>
          <Form.Item name="confirm" rules={[{ required: true, message: t('register.confirmRequired') }]}>
            <div style={registerPageStyles.inputShell}>
              <div style={registerPageStyles.inputIcon}>
                <LockOutlined />
              </div>
              <Input placeholder={t('register.confirmPlaceholder')} type="password" clearable style={registerPageStyles.input} />
            </div>
          </Form.Item>
        </Form>
        </div>
        <div style={registerPageStyles.footer}>
          {t('register.haveAccount')}
          <span style={registerPageStyles.footerLink} onClick={() => navigate('/login')}>
            {t('register.goLogin')}
          </span>
        </div>
      </div>
    </div>
  );
};
